/**
 * HamburgerMenu — the single, unified "Access All Areas" panel used by
 * BOTH Landing and Dashboard. Owen Feb 2026: "Let's just do one
 * hamburger menu — same one for both sides except admin-only items."
 *
 * What's intentionally NOT in the menu for non-admins:
 *   • Reading Room — admin-only entry under "Behind the curtain".
 *     Owen Feb 2026: this is the curated public scroll he shares with
 *     investors/partners. Not for the average user.
 *
 * Sections, in this order:
 *   1) Top header — crossed-swords home, "Access All Areas" eyebrow,
 *      calligraphic quill arrow, and the gold-door SecretEntry pill.
 *   2) Personal — appears only when currentUser is set.
 *   3) Community — always visible.
 *   4) Founder / Admin — gated to is_founder / is_admin respectively.
 *   5) Footer row — LanguageSwitcher, optional Sign Out (when logged in).
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import SecretEntry from '@/components/SecretEntry';
import {
  Crown, Home, TreePine, PenTool, Feather, Swords, BookMarked, Library,
  Mail, Vault, Hourglass, BookOpen, Users, Trophy, Link2, BookHeart,
  Shield, LogOut, Smartphone, Sparkles, Mic, ScrollText
} from 'lucide-react';

const SectionLabel = ({ children }) => (
  <div className="flex items-center gap-2 px-2 mt-1 mb-1.5">
    <div className="h-px flex-1 bg-[#3D3531]" />
    <span className="text-[10px] uppercase tracking-[0.32em] text-[#8A817C]" style={{fontFamily: 'Work Sans, sans-serif'}}>{children}</span>
    <div className="h-px flex-1 bg-[#3D3531]" />
  </div>
);

const MenuItem = ({ icon: Icon, label, onClick, gold = false, testid }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg transition-colors text-left
      ${gold ? 'text-[#D4AF37] hover:bg-[#D4AF37]/10' : 'text-[#C4B8AE] hover:bg-white/5 hover:text-[#F4EFE6]'}`}
  >
    <Icon className={`w-4 h-4 shrink-0 ${gold ? 'text-[#D4AF37]' : 'text-[#9C8E83]'}`} />
    <span className="text-sm font-medium truncate" style={{fontFamily: 'Work Sans, sans-serif'}}>{label}</span>
  </button>
);

/**
 * Props:
 *   currentUser  – the auth context user object (or null when logged out)
 *   onClose      – called whenever a menu item is tapped, before navigation
 *   onLogout     – optional sign-out handler (Dashboard supplies this)
 *   onFounderCoins – optional, opens the Premium Access Coins modal
 *   onTroubleSigningIn – optional, opens the webview warning (Landing)
 */
const HamburgerMenu = ({
  currentUser,
  onClose = () => {},
  onLogout,
  onFounderCoins,
  onTroubleSigningIn,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const go = (path) => { onClose(); navigate(path); };

  return (
    <div className="theme-fixed bg-[#1C1917] border-t border-[#3D3531] shadow-lg max-h-[80vh] overflow-y-auto" data-testid="hamburger-menu">
      {/* ── Header strip ───────────────────────────────────────── */}
      <div className="px-4 pt-3 pb-2 sticky top-0 bg-[#1C1917] z-10 flex items-center gap-3">
        <button
          type="button"
          onClick={() => go('/')}
          data-testid="menu-swords-home"
          aria-label="Return to home page"
          title="Home"
          className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 rounded-md bg-[#2A2522] border border-[#D4AF37]/40 text-[#D4AF37] hover:border-[#D4AF37] hover:bg-[#1C1917] hover:text-[#E8C5A0] hover:shadow-[0_0_10px_rgba(212,175,55,0.3)] transition-all flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/60"
        >
          <Swords className="w-4 h-4 sm:w-[18px] sm:h-[18px]" />
        </button>

        <p className="text-xs uppercase tracking-[0.2em] text-[#E8C5A0] font-medium flex-1" style={{fontFamily: 'Work Sans, sans-serif'}}>Access All Areas</p>

        {/* Calligraphic gold quill arrow pointing toward the door */}
        <span aria-hidden="true" className="shrink-0 inline-flex items-center text-[#D4AF37]">
          <svg viewBox="0 0 80 28" className="w-16 sm:w-20 h-7 sm:h-8 block" fill="none" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="hamburgerQuillGold" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#9F7B2C" stopOpacity="0.4" />
                <stop offset="40%" stopColor="#D4AF37" />
                <stop offset="100%" stopColor="#E8C5A0" />
              </linearGradient>
            </defs>
            <path d="M3 15 Q 22 9, 60 13 L 66 13" stroke="url(#hamburgerQuillGold)" strokeWidth="1.7" strokeLinecap="round" fill="none" />
            <path d="M8 12 Q 12 9, 16 12"   stroke="url(#hamburgerQuillGold)" strokeWidth="1"   strokeLinecap="round" fill="none" opacity="0.9" />
            <path d="M14 11 Q 18 8.5, 22 11" stroke="url(#hamburgerQuillGold)" strokeWidth="1"   strokeLinecap="round" fill="none" opacity="0.9" />
            <path d="M20 10.5 Q 24 8, 28 10.5" stroke="url(#hamburgerQuillGold)" strokeWidth="0.95" strokeLinecap="round" fill="none" opacity="0.85" />
            <path d="M26 10.5 Q 30 8.2, 34 10.7" stroke="url(#hamburgerQuillGold)" strokeWidth="0.95" strokeLinecap="round" fill="none" opacity="0.85" />
            <path d="M32 11 Q 36 8.7, 40 11.2" stroke="url(#hamburgerQuillGold)" strokeWidth="0.95" strokeLinecap="round" fill="none" opacity="0.8" />
            <path d="M38 11.4 Q 42 9.2, 46 11.6" stroke="url(#hamburgerQuillGold)" strokeWidth="0.95" strokeLinecap="round" fill="none" opacity="0.8" />
            <path d="M44 11.8 Q 48 9.7, 52 12" stroke="url(#hamburgerQuillGold)" strokeWidth="0.9" strokeLinecap="round" fill="none" opacity="0.75" />
            <path d="M10 17 Q 14 18.5, 18 17" stroke="url(#hamburgerQuillGold)" strokeWidth="0.7" strokeLinecap="round" fill="none" opacity="0.55" />
            <path d="M18 17.5 Q 22 19, 26 17.5" stroke="url(#hamburgerQuillGold)" strokeWidth="0.7" strokeLinecap="round" fill="none" opacity="0.5" />
            <path d="M66 13 L 76 13 M 70 8 L 76 13 L 70 18" stroke="url(#hamburgerQuillGold)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" fill="none" />
          </svg>
        </span>

        <SecretEntry onClick={onClose} />
      </div>

      <nav className="px-2 pt-1 pb-4" aria-label="Site sections">
        {/* ── Personal — only when logged in ────────────────────── */}
        {currentUser && (
          <>
            <SectionLabel>{t('hmenu.section_personal', 'Personal')}</SectionLabel>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-1 mb-3">
              <MenuItem icon={Crown}      label={t('hmenu.dashboard', 'Dashboard')}            gold onClick={() => go('/dashboard')}                              testid="hmenu-dashboard" />
              <MenuItem icon={Home}       label={t('hmenu.home', 'Home')}                      onClick={() => go('/')}                                            testid="hmenu-home" />
              <MenuItem icon={TreePine}   label={t('hmenu.family_legacy_scroll', 'Family Legacy Scroll')} onClick={() => go(`/family-tree/${currentUser.user_id}`)} testid="hmenu-family-tree" />
              <MenuItem icon={PenTool}    label={t('hmenu.daily_journal', 'Daily Journal')}    onClick={() => go('/journal')}                                     testid="hmenu-journal" />
              <MenuItem icon={Feather}    label={t('hmenu.write_your_story', 'Write Your Story')} onClick={() => go('/authors-hub')}                              testid="hmenu-write" />
              <MenuItem icon={Swords}     label={t('hmenu.enter_legends', 'Enter Legends')}    gold onClick={() => go('/enter-arena')}                            testid="hmenu-enter-arena" />
              <MenuItem icon={BookMarked} label={t('hmenu.my_stories', 'My Stories')}          onClick={() => go('/my-stories')}                                  testid="hmenu-my-stories" />
              <MenuItem icon={Library}    label={t('hmenu.my_library', 'My Library')}          onClick={() => go('/library-hub')}                                 testid="hmenu-library" />
              <MenuItem icon={Mic}        label={t('hmenu.voice_library', 'Voice Library')}    gold onClick={() => go('/voice-library')}                          testid="hmenu-voice-library" />
              <MenuItem icon={Mail}       label={t('hmenu.read_requests', 'Read Requests')}    onClick={() => go('/read-requests')}                               testid="hmenu-read-requests" />
              <MenuItem icon={Vault}      label={t('hmenu.deep_vault_retreat', 'Deep Vault Retreat')} onClick={() => go('/dvrt')}                                 testid="hmenu-dvrt" />
              <MenuItem icon={Hourglass}  label={t('hmenu.time_capsules', 'Time Capsules')}    gold onClick={() => go('/capsules')}                               testid="hmenu-capsules" />
            </div>
          </>
        )}

        {/* ── Community — always visible ────────────────────────── */}
        <SectionLabel>{t('hmenu.section_community', 'Community')}</SectionLabel>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-1 mb-3">
          <MenuItem icon={Feather}   label={t('hmenu.authors_corner', "Author's Corner")} onClick={() => go('/authors')}                                 testid="hmenu-authors" />
          <MenuItem icon={BookOpen}  label={t('hmenu.browse', 'Browse')}                  onClick={() => go('/browse')}                                  testid="hmenu-browse" />
          <MenuItem icon={Users}     label={t('hmenu.family_archives', 'Family Archives')} onClick={() => go('/families')}                               testid="hmenu-families" />
          <MenuItem icon={Trophy}    label={t('hmenu.highly_appreciated', 'Highly Appreciated')} onClick={() => go('/highly-appreciated')}              testid="hmenu-highly-appreciated" />
          <MenuItem icon={Trophy}    label={t('hmenu.story_of_the_week', 'Story of the Week')}  onClick={() => go('/story-of-the-week')}               testid="hmenu-sotw" />
          <MenuItem icon={Swords}    label={t('hmenu.legends_leaderboard', 'Legends Leaderboard')} gold onClick={() => go('/arena')}                    testid="hmenu-arena-leaderboard" />
          <MenuItem icon={Crown}     label={t('hmenu.hall_of_champions', 'Hall of Champions')} gold onClick={() => go('/hall-of-champions')}             testid="hmenu-hall-of-champions" />
          <MenuItem icon={BookHeart} label={t('hmenu.discover_binding', 'Discover Binding')} gold onClick={() => go('/discover-binding')}                testid="hmenu-discover-binding" />
          <MenuItem icon={Link2}     label={t('hmenu.legacy_links', 'Legacy Links')}      gold onClick={() => go('/links')}                              testid="hmenu-legacy-links" />
          <MenuItem icon={Crown}     label={t('hmenu.premium', 'Premium')}                gold onClick={() => go('/premium')}                            testid="hmenu-premium" />
          {/* Owen Feb 2026 — Legend Original ($1,000 lifetime, 100 seats)
              and Founder I ($10,000 single seat) surfaces. Previously only
              discoverable via the desktop landing hero medallion which is
              hidden on mobile. Adding them here so they're reachable from
              every screen. Both marked `gold` for prestige emphasis. */}
          <MenuItem icon={Trophy}    label={t('hmenu.legend_original', 'Legend Original')} gold onClick={() => go('/legend')}                              testid="hmenu-legend-original" />
          <MenuItem icon={Crown}     label={t('hmenu.founder_i', 'Founder I')}             gold onClick={() => go('/founder-i')}                           testid="hmenu-founder-i" />
          {currentUser && (
            <MenuItem icon={Users}   label={t('hmenu.family_plan', 'Family Plan')}        gold onClick={() => go('/family-plan')}                        testid="hmenu-family-plan" />
          )}
        </div>

        {/* ── Founder / Admin ────────────────────────────────────── */}
        {(currentUser?.is_founder || currentUser?.is_admin) && (
          <>
            <SectionLabel>{t('hmenu.section_behind_the_curtain', 'Behind the curtain')}</SectionLabel>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-1 mb-3">
              {currentUser?.is_founder && onFounderCoins && (
                <MenuItem icon={Sparkles} label={t('hmenu.premium_access_coins', 'Premium Access Coins')} gold onClick={() => { onClose(); onFounderCoins(); }} testid="hmenu-premium-coins" />
              )}
              {currentUser?.is_admin && (
                <MenuItem icon={Shield}   label={t('hmenu.admin_panel', 'Admin Panel')} onClick={() => go('/admin')}                                    testid="hmenu-admin" />
              )}
              {/* Owen Feb 28, 2026 round 62 — Reading Room is restored to
                  the admin section. It's the curated public scroll Owen
                  shares with investors/partners. ADMIN-ONLY by design —
                  not for the average user. The header comment above this
                  file used to claim the dashboard taskbar covered this;
                  that taskbar entry was lost in an earlier UI pass and
                  Owen wants the hamburger entry as the canonical home. */}
              {currentUser?.is_admin && (
                <MenuItem icon={ScrollText} label={t('hmenu.reading_room', 'Reading Room')} onClick={() => go('/reading-room')} testid="hmenu-reading-room" />
              )}
            </div>
          </>
        )}

        {/* Anonymous-viewer helper */}
        {!currentUser && onTroubleSigningIn && (
          <button
            onClick={() => { onClose(); onTroubleSigningIn(); }}
            className="w-full text-left px-3 py-2.5 rounded-lg text-[#8A817C] hover:bg-white/10 transition-colors flex items-center gap-3"
            data-testid="hmenu-trouble-signin"
          >
            <Smartphone className="w-4 h-4 text-[#8A817C]" />
            <span className="text-sm italic" style={{fontFamily: 'Cormorant Garamond, serif'}}>{t('hmenu.trouble_signing_in', 'Trouble signing in? Open in browser')}</span>
          </button>
        )}
      </nav>

      {/* ── Footer row ────────────────────────────────────────────── */}
      <div className="px-4 pt-3 pb-4 border-t border-[#3D3531] flex items-center justify-between gap-2">
        <LanguageSwitcher dropUp />
        {currentUser && onLogout && (
          <button
            onClick={onLogout}
            className="px-3 py-2 rounded-lg text-[#D4AF37] hover:bg-[#2A2522] hover:text-[#E8C5A0] transition-colors flex items-center gap-2"
            data-testid="hmenu-logout"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">{t('hmenu.sign_out', 'Sign Out')}</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default HamburgerMenu;
